import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CalendarClock, Loader2, Pause, Pencil, Play, RefreshCw, Send, XCircle } from "lucide-react";
import { emailApi, isAbort, type EmailCohort, type MessageDetail } from "./api";
import DraftForm from "./draft-form";
import { Eyebrow, Notice, Skeleton, StatusPill, btnGhost, btnPrimary, fmtDate, inputCls } from "./shared";

export default function MessageReview({ id, cohorts, onChanged }: { id: number; cohorts: EmailCohort[]; onChanged: () => void }) {
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [ack, setAck] = useState(false);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const ctrl = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    ctrl.current?.abort();
    const c = new AbortController(); ctrl.current = c;
    setError(null);
    try {
      const d = await emailApi<MessageDetail>(`/messages/${id}`, { signal: c.signal });
      if (!c.signal.aborted) setDetail(d);
    } catch (e) {
      if (!isAbort(e)) setError(e instanceof Error ? e.message : "Could not load this message.");
    }
  }, [id]);

  useEffect(() => { setDetail(null); setEditing(false); setAck(false); setWhen(""); setActionError(null); void load(); return () => ctrl.current?.abort(); }, [load]);

  if (error) return <div className="space-y-3"><Notice tone="error">{error}</Notice><button data-testid="button-retry-detail" className={btnGhost} onClick={load}><RefreshCw className="w-4 h-4" /> Retry</button></div>;
  if (!detail) return <Skeleton rows={5} />;

  const m = detail.message;
  const eligible = detail.recipients.filter((r) => !r.eligibility);
  const excluded = detail.recipients.filter((r) => r.eligibility);
  const sendStarted = m.sent > 0 || m.failed > 0 || m.status === "sending" || m.status === "completed";

  const act = async (action: "approve" | "pause" | "cancel" | "resume") => {
    if (action === "cancel" && !confirm("Cancel this message? Anything already handed to the provider cannot be recalled.")) return;
    setBusy(action); setActionError(null);
    try {
      const body: Record<string, unknown> = { action, version: m.version };
      if (action === "approve") { body.acknowledge = true; if (when) body.scheduledAt = new Date(when).toISOString(); }
      await emailApi(`/messages/${m.id}/action`, { method: "POST", body: JSON.stringify(body) });
      setAck(false); await load(); onChanged();
    } catch (e) { setActionError(e instanceof Error ? e.message : "Action failed."); }
    finally { setBusy(null); }
  };

  if (editing) {
    return (
      <DraftForm
        title="Edit draft"
        cohorts={cohorts}
        messageId={m.id}
        version={m.version}
        initial={{ subject: m.subject, bodyText: m.body_text, to: detail.recipients.map((r) => r.email), request: m.request || undefined }}
        onSaved={() => { setEditing(false); void load(); onChanged(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const html = m.payload?.html;
  const text = m.payload?.text || m.body_text;

  return (
    <div className="space-y-6" data-testid={`review-message-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow>{m.source}{m.essential ? " · essential" : " · marketing"} · v{m.version}</Eyebrow>
          <h3 className="mt-1 font-serif text-2xl leading-tight tracking-tight break-words" data-testid="text-review-subject">{m.subject}</h3>
          <p className="mt-1 text-xs text-muted-foreground">Created {fmtDate(m.created_at)}{m.created_by ? ` by ${m.created_by}` : ""}{m.approved_at ? ` · approved ${fmtDate(m.approved_at)}${m.approved_by ? ` by ${m.approved_by}` : ""}` : ""}</p>
        </div>
        <div className="flex items-center gap-2"><StatusPill status={m.status} /><button data-testid="button-refresh-detail" onClick={load} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button></div>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px overflow-hidden rounded-lg border border-border bg-border text-center">
        {[["Recipients", m.total], ["Sent", m.sent], ["Excluded", m.excluded], ["Failed", m.failed]].map(([k, v]) => (
          <div key={k} className="bg-card px-3 py-2.5"><dt className="text-[11px] text-muted-foreground">{k}</dt><dd className="font-mono text-lg">{v}</dd></div>
        ))}
      </dl>
      {m.scheduled_at && <p className="flex items-center gap-2 text-sm"><CalendarClock className="w-4 h-4 text-[#FF5C00]" /> Scheduled for {fmtDate(m.scheduled_at)}</p>}
      {m.request && <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Request:</span> {m.request}</p>}

      <div>
        <Eyebrow>Exact content{m.payload?.from ? ` · from ${m.payload.from}` : ""}</Eyebrow>
        {html ? (
          <iframe title="Email preview" sandbox="" srcDoc={`<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">${html}`} className="mt-2 h-[420px] w-full rounded-lg border border-border bg-[#FBFAF7]" data-testid="iframe-email-preview" />
        ) : (
          <pre className="mt-2 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-4 text-sm leading-relaxed font-sans" data-testid="text-email-body">{text}</pre>
        )}
      </div>

      <div>
        <Eyebrow>Recipients · {eligible.length} eligible, {excluded.length} will be excluded</Eyebrow>
        <div className="mt-2 max-h-[340px] overflow-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-[11px] text-muted-foreground"><tr><th className="px-3 py-2 text-left font-medium">Person</th><th className="px-3 py-2 text-left font-medium">Status</th><th className="px-3 py-2 text-left font-medium">Eligibility now</th></tr></thead>
            <tbody className="divide-y divide-border">
              {detail.recipients.map((r) => (
                <tr key={r.id} data-testid={`row-recipient-${r.id}`} className={r.eligibility ? "bg-red-500/[0.04]" : ""}>
                  <td className="px-3 py-2"><p className="font-medium break-all">{r.email}</p>{r.name && <p className="text-xs text-muted-foreground">{r.name}</p>}</td>
                  <td className="px-3 py-2 text-xs"><span>{r.status}</span>{r.event && <span className="text-muted-foreground"> · {r.event}</span>}{r.reason && <p className="text-muted-foreground">{r.reason}</p>}</td>
                  <td className="px-3 py-2 text-xs">{r.eligibility ? <span className="text-red-700 dark:text-red-300">Excluded: {r.eligibility}</span> : <span className="text-emerald-700 dark:text-emerald-300">Eligible</span>}</td>
                </tr>
              ))}
              {detail.recipients.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground">No recipients resolved.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {actionError && <Notice tone="error">{actionError}</Notice>}

      {(m.status === "draft" || m.status === "needs_attention") && (
        <div className="rounded-xl border border-[#FF5C00]/30 bg-[#FF5C00]/[0.05] p-4 space-y-3">
          <p className="text-sm font-semibold">Approve this exact snapshot</p>
          <p className="text-sm text-muted-foreground">Once approved, this content goes automatically to the {eligible.length} eligible recipient{eligible.length === 1 ? "" : "s"} when due, within pacing limits. Excluded people are skipped. Eligibility is checked again at send time.</p>
          <label className="block space-y-1 max-w-xs">
            <span className="text-xs font-semibold text-muted-foreground">Send time (optional, your local time)</span>
            <input data-testid="input-schedule" type="datetime-local" className={inputCls} value={when} onChange={(e) => setWhen(e.target.value)} />
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input data-testid="checkbox-acknowledge" type="checkbox" className="mt-1 accent-[#FF5C00]" checked={ack} onChange={(e) => setAck(e.target.checked)} />
            <span>I have reviewed the exact content, all {detail.recipients.length} recipients and every exclusion reason above.</span>
          </label>
          <div className="flex flex-wrap gap-2">
            <button data-testid="button-approve" disabled={!ack || !!busy || eligible.length === 0} onClick={() => act("approve")} className={btnPrimary}>
              {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : when ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {when ? "Approve and schedule" : "Approve and send"}
            </button>
            {!sendStarted && <button data-testid="button-edit" onClick={() => setEditing(true)} className={btnGhost}><Pencil className="w-4 h-4" /> Edit</button>}
            <button data-testid="button-cancel-message" disabled={!!busy} onClick={() => act("cancel")} className={btnGhost}><XCircle className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {(m.status === "approved" || m.status === "sending") && (
        <div className="space-y-2">
          <Notice tone="warn"><AlertTriangle className="inline w-4 h-4 mr-1" />Pausing stops further sends. Emails already handed to the provider cannot be recalled.</Notice>
          <div className="flex flex-wrap gap-2">
            <button data-testid="button-pause" disabled={!!busy} onClick={() => act("pause")} className={btnGhost}>{busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />} Pause</button>
            {!sendStarted && <button data-testid="button-edit" onClick={() => setEditing(true)} className={btnGhost}><Pencil className="w-4 h-4" /> Edit (resets approval)</button>}
            <button data-testid="button-cancel-message" disabled={!!busy} onClick={() => act("cancel")} className={btnGhost}><XCircle className="w-4 h-4" /> Cancel remaining</button>
          </div>
        </div>
      )}

      {m.status === "paused" && (
        <div className="flex flex-wrap gap-2">
          <button data-testid="button-resume" disabled={!!busy} onClick={() => act("resume")} className={btnPrimary}>{busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Resume sending</button>
          <button data-testid="button-cancel-message" disabled={!!busy} onClick={() => act("cancel")} className={btnGhost}><XCircle className="w-4 h-4" /> Cancel remaining</button>
        </div>
      )}

      <div>
        <Eyebrow>Audit trail</Eyebrow>
        <ol className="mt-2 space-y-2 border-l border-border pl-4">
          {detail.events.map((ev, i) => (
            <li key={i} className="text-sm"><span className="font-medium">{ev.action}</span>{ev.actor && <span className="text-muted-foreground"> by {ev.actor}</span>}<span className="block text-xs text-muted-foreground">{fmtDate(ev.created_at)}</span></li>
          ))}
          {detail.events.length === 0 && <li className="text-sm text-muted-foreground">No events recorded yet.</li>}
        </ol>
      </div>
    </div>
  );
}
